import React, { useContext } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Context } from 'Context/Context';
import { TEAMUP_ENABLED } from 'Constants';
import useIsMobile from 'Hooks/useIsMobile';
import { getCaseType, getNetWorth } from 'Utils';
import { C, Svg, Tip } from './CaseListUI';
import { clientNameOf } from './CaseStage';

// ---------------------------------------------------------------------------
// Case card for every attorney case list (design 23c desktop / 22e mobile).
// Content comes straight from the case document; list-specific bits (notices,
// action buttons, "requested by team …") are passed in as props so each list
// keeps its own behaviour.
// ---------------------------------------------------------------------------

export const money = (v) => Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const estimateOf = (d) => {
  const lo = d?.aiEstimateLowerValue != null ? d.aiEstimateLowerValue : d?.aiEstimate?.lowerValue;
  const hi = d?.aiEstimateUpperValue != null ? d.aiEstimateUpperValue : d?.aiEstimate?.upperValue;
  if (lo == null || hi == null || isNaN(Number(lo)) || isNaN(Number(hi))) return null;
  return `${money(lo)} – ${money(hi)}`;
};

export const difficultyWord = (v) => (v <= 6 ? 'Low' : v <= 8 ? 'Medium' : 'Hard');
export const suitabilityWord = (v) => (v <= 4 ? 'Low' : v <= 7 ? 'Medium' : 'High');
export const place = (city, state, zip) => [city, state, zip].filter((x) => x !== undefined && x !== null && String(x).trim() !== '').join(', ');
const placeShort = (city, state, zip) => [city, [state, zip].filter(Boolean).join(' ')].filter((x) => x && String(x).trim() !== '').join(', ');
export const yesNo = (v) => (v === 'yes' ? 'Yes' : 'No');
export const publicCo = (v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : 'Unknown');

// Requests / In review — same counting as the old ProfessionalsInvolved.
export const reqReviewOf = (reqRevCountArr, caseId) => {
  const row = reqRevCountArr?.[caseId];
  if (!row) return { requests: 0, review: 0 };
  let requests = 0; let review = 0;
  Object.entries(row).forEach(([k, v]) => {
    const n = parseInt(v) || 0;
    requests += n;
    if (k !== 'requested' && k !== 'denied' && k !== 'conflict') review += n;
  });
  return { requests, review };
};

export const ICON = {
  calendar: ['M3 5h18v16H3z', 'M3 9h18M8 3v4M16 3v4'],
  clock: ['M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M12 9v4l2.5 2M9 2h6'],
  pin: ['M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11z', 'M12 12.3a2.3 2.3 0 1 0 0-4.6 2.3 2.3 0 0 0 0 4.6z'],
  home: ['M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z'],
  info: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 11v5M12 8h.01'],
  lock: ['M5 11h14v10H5z', 'M8 11V7a4 4 0 0 1 8 0v4'],
  person: ['M12 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z', 'M5 21c0-4 3-6.5 7-6.5s7 2.5 7 6.5'],
  people: ['M9 11.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z', 'M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5', 'M16 5a3 3 0 0 1 0 6M18 14.8c1.8.7 3 2.5 3 5.2'],
  eye: ['M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
  bookmark: ['M6 3h12v18l-6-4-6 4z'],
};

export function Blur({ children }) {
  return <span className="blur-[4px] select-none">{children}</span>;
}

// ---------------------------------------------------------------------------
// Free law firm accounts (design 1b "one premium band"): locked AI insights are
// never shown as blurred values - they are listed by name in one brass band with
// a single "Unlock insights" button. /case/listCases leaves the values out for
// these accounts and sends lockedFields (which insights the case has).
// ---------------------------------------------------------------------------
export const LOCKED_INSIGHTS = [
  ['estimate', 'Case estimate'],
  ['deadline', 'Deadline to file'],
  ['classAction', 'Class action'],
  ['plaintiffs', 'Multiple plaintiffs'],
  ['netWorth', 'Adverse party net-worth'],
  ['entity', 'Entity/Individual'],
  ['publicCompany', 'Against public company'],
];
const hasVal = (v) => v !== undefined && v !== null && v !== '';
// fallback for lists that still send the values: which insights the case has
const lockedInsightsOf = (d) => {
  const has = {
    estimate: estimateOf(d) != null,
    deadline: hasVal(d.aiDate),
    classAction: hasVal(d.isClassAction),
    plaintiffs: hasVal(d.defendant),
    netWorth: hasVal(d.adversePartyNetWorth),
    entity: hasVal(d.isEntity),
    publicCompany: hasVal(d.adversePartyPublicCompany),
  };
  return LOCKED_INSIGHTS.map(([k]) => k).filter((k) => has[k]);
};

function LockIcon({ size }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" className="flex-none">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
function CrownIcon({ size, className = '' }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={`flex-none ${className}`}>
      <path d="M3 7l4.5 4L12 5l4.5 6L21 7l-2 11H5z" />
    </svg>
  );
}

function PremiumBand({ keys, onUnlock, note, isMobile, className = '' }) {
  const items = LOCKED_INSIGHTS.filter(([k]) => keys.includes(k));
  if (!items.length) return null;
  const n = items.length;
  return (
    <div role="group" aria-label="Premium insights, locked"
      className={`flex bg-brass/[.06] border border-brass/[.28] rounded-md py-4 px-5 ${isMobile ? 'flex-col items-stretch gap-4' : 'flex-row justify-between items-center gap-6 max-[900px]:flex-col max-[900px]:items-stretch max-[900px]:gap-4'} ${className}`}>
      <div className="flex flex-col gap-3 min-w-0">
        <div className="flex items-center gap-2 font-plexsans font-semibold text-[11px] leading-[1.3] tracking-[.08em] uppercase text-brass">
          <CrownIcon size={14} />{n} premium insight{n === 1 ? '' : 's'} on this case
        </div>
        <ul className="flex flex-wrap gap-y-2.5 gap-x-[22px] m-0 p-0 list-none">
          {items.map(([k, label]) => (
            <li key={k} className={`flex items-center gap-2 font-plexsans font-medium ${isMobile ? 'text-[12.5px]' : 'text-[13px]'} leading-[1.3] text-navy whitespace-nowrap`}>
              <span className="flex text-brass"><LockIcon size={12} /></span>{label}
            </li>
          ))}
        </ul>
      </div>
      <button type="button" onClick={onUnlock} title={note}
        className={`flex-none inline-flex items-center justify-center gap-[7px] py-3 px-[18px] rounded-md border-0 bg-navy hover:bg-[#0d1528] text-ivory font-plexsans font-semibold text-[13px] leading-none whitespace-nowrap cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2 ${isMobile ? 'w-full h-11' : 'max-[900px]:w-full'}`}>
        <CrownIcon size={14} className="text-gold" />Unlock insights
      </button>
    </div>
  );
}

// Pill (difficulty / suitability) as classes - same look as pill() below.
const PILL = 'inline-flex items-center gap-1.5 font-plexsans font-semibold text-[11.5px] leading-none border rounded-[20px] py-1.5 px-3 whitespace-nowrap';
// suitability tones: text / background / border
const TONE = {
  green: { text: 'text-success', bg: 'bg-success/[.08]', border: 'border-success/[.35]' },
  red: { text: 'text-danger', bg: 'bg-danger/[.07]', border: 'border-danger/[.3]' },
  navy: { text: 'text-navy', bg: 'bg-navy/[.05]', border: 'border-navy/[.15]' },
};
const suitTone = (v) => (v > 7 ? TONE.green : v <= 4 ? TONE.red : TONE.navy);

function CaseCard({
  detail,              // case document (caseDetails)
  userInfo,            // whose subscription decides what is unlocked
  reqRevCountArr,
  notices,             // node(s) shown at the top of the body
  headerExtra,         // node shown next to the "Posted by" pill (e.g. list icons)
  showNames = false,   // potential client / adverse party (active + closed lists)
  names,               // { client, adverse } override for showNames
  hideStats = false,   // hide Requests / In review
  footerNote,          // e.g. "Requested by team …"
  actions,             // node, right side of the footer
  onOpen,              // click on the card (desktop) opens the case
  dimmed = false,
  overlay,             // node absolutely positioned over the card
  headerPill,          // node replacing the "Posted by client · date" pill (e.g. "Approved by client · date")
  expanded,            // node shown inside the card below the footer (e.g. the requested-case status)
}) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { userDetails } = useContext(Context);
  const premium = !!userInfo?.isActiveSubscription;
  const d = detail || {};
  const estimate = estimateOf(d);
  const posted = d.created_at || d.createdAt;
  const postedBy = d.isPostedBySelf === 'yes' ? 'Posted by client' : d.isPostedBySelf ? 'Posted for client' : 'Posted';
  const { requests, review } = reqReviewOf(reqRevCountArr, d._id);
  const deadline = d.aiDate ? moment(d.aiDate) : null;
  const deadlineUrgent = deadline ? deadline.diff(moment(), 'months', true) < 6 : false;
  const upsell = userDetails?.isUserAssistant ? 'The attorney needs a Premium membership to see this.' : 'Subscribe to Premium to see this.';
  // Law firm accounts: the client saved this firm from their AI firm matches.
  // Free firms only ever see cases that saved them (see /case/listCases), so every
  // card gets it; premium firms need the server to flag it (savedByClient).
  const savedByClient = userInfo?.role === 5 && (d.savedByClient === true || (!premium && d.savedByClient !== false));
  const savedTag = savedByClient ? (
    <span title="The client saved your firm from their AI firm matches"
      className={`inline-flex items-center gap-1.5 font-plexsans font-semibold ${isMobile ? 'text-[11px] py-[5px] px-[9px]' : 'text-[12px] py-[7px] px-3'} leading-none text-brass bg-white border border-brass/[.35] rounded-[20px] whitespace-nowrap`}>
      <Svg d={ICON.bookmark} size={isMobile ? 11 : 12} width={2.2} stroke={C.brass} />
      Client saved your firm
    </span>
  ) : null;
  const goSubscribe = (e) => { e.stopPropagation(); if (!userDetails?.isUserAssistant) navigate('/attorney/subscription'); };

  // free law firm account -> design 1b (Premium band) instead of blurred values
  const lockedFirm = userInfo?.role === 5 && !premium;
  const lockedKeys = lockedFirm ? (Array.isArray(d.lockedFields) ? d.lockedFields : lockedInsightsOf(d)) : [];
  const ownerNote = userDetails?.isUserAssistant ? 'The firm account owner can upgrade to Premium.' : 'Upgrade to Premium to see these insights.';
  const unlock = (e) => {
    e.stopPropagation();
    if (userDetails?.isUserAssistant) { toast.info(ownerNote); return; }
    navigate(`/attorney/subscription?from=case-card&case=${encodeURIComponent(d.caseNumber || '')}`);
  };
  const band = lockedFirm ? <PremiumBand keys={lockedKeys} onUnlock={unlock} note={ownerNote} isMobile={isMobile} /> : null;

  const copyId = (e) => {
    e.stopPropagation();
    if (!d.caseNumber) return;
    try { navigator.clipboard.writeText(d.caseNumber); toast.success('Case number copied'); } catch (err) { /* ignore */ }
  };

  // ---- pieces ----
  const estimatePending = <span className="text-navy/[.45] font-medium">Estimate pending</span>;
  const estimateLocked = !lockedFirm ? null : lockedKeys.includes('estimate') ? (
    <span aria-label="Case estimate locked — Premium" title={ownerNote}
      className={`inline-flex items-center gap-1.5 ${isMobile ? 'py-[5px] px-[9px] text-[12px]' : 'py-2 px-[11px] text-[13px]'} rounded-[5px] bg-brass/[.08] border border-dashed border-brass/[.45] font-plexsans font-semibold leading-none text-brass whitespace-nowrap`}>
      <LockIcon size={isMobile ? 12 : 13} />Estimate
    </span>
  ) : estimatePending;
  const estimateNode = premium
    ? (estimate || estimatePending)
    : <span onClick={goSubscribe} title={upsell} className="cursor-pointer">$<Blur>000,000</Blur> – $<Blur>000,000</Blur></span>;

  const difficulty = d.aiDifficulty ? (
    <Tip text="AI rating of how complex this case may be to litigate, based on the case details.">
      <span className={`${PILL} text-navy bg-navy/[.05] border-navy/[.15]`}>
        Difficulty: {d.aiDifficulty}/10 ({difficultyWord(d.aiDifficulty)})
        <span className="flex opacity-70"><Svg d={ICON.info} size={12} width={2} /></span>
      </span>
    </Tip>
  ) : null;

  // litigation funder suitability is hidden until funders launch (TEAMUP_ENABLED)
  const showSuit = TEAMUP_ENABLED && !!d.aiSuitability;
  const suitability = showSuit ? (premium ? (
    <Tip text="AI rating of how likely a litigation funder is to be interested in funding this case.">
      <span className={`${PILL} ${suitTone(d.aiSuitability).text} ${suitTone(d.aiSuitability).bg} ${suitTone(d.aiSuitability).border}`}>
        Litigation funder suitability: {d.aiSuitability}/10 ({suitabilityWord(d.aiSuitability)})
        <span className="flex opacity-70"><Svg d={ICON.info} size={12} width={2} /></span>
      </span>
    </Tip>
  ) : (
    <Tip text={upsell}>
      <span onClick={goSubscribe} className={`${PILL} text-brass bg-brass/[.08] border-brass/[.3] cursor-pointer`}>
        Litigation funder suitability: <Blur>00</Blur>/10
        <span className="flex"><Svg d={ICON.lock} size={12} width={2.2} /></span>
      </span>
    </Tip>
  )) : null;

  const deadlineVal = !premium
    ? <span onClick={goSubscribe} title={upsell} className="cursor-pointer"><Blur>{isMobile ? 'Sep 09, 2028' : 'September 09, 2028'}</Blur></span>
    : deadline ? deadline.format(isMobile ? 'MMM DD, YYYY' : 'MMMM DD, YYYY') : 'N/A';

  const rows = [
    { icon: ICON.calendar, label: 'Date of incident', short: 'Incident date', value: d.dateInfo ? moment(d.dateInfo).format('MM/DD/YY') : '—' },
    // free law firms: the deadline is a Premium insight (listed in the band)
    ...(lockedFirm ? [] : [{ icon: ICON.clock, label: 'Deadline to file lawsuit', short: 'File-by deadline', value: deadlineVal, urgent: premium && deadlineUrgent, tip: 'AI estimate of the deadline to file a lawsuit.' }]),
    { icon: ICON.pin, label: 'Location of incident', short: 'Incident location', value: isMobile ? placeShort(d.caseCity, d.caseState, d.caseZipcode) : place(d.caseCity, d.caseState, d.caseZipcode), cap: true },
    { icon: ICON.home, label: 'Location of client', short: 'Client location', value: isMobile ? placeShort(d.clientCity, d.clientState, d.clientZipcode) : place(d.clientCity, d.clientState, d.clientZipcode), cap: true },
  ];

  const tagList = [
    ['Class action', `Class action: ${yesNo(d.isClassAction)}`, 'Class action'],
    ['Multiple plaintiffs', `Multiple plaintiffs: ${yesNo(d.defendant)}`, 'Multiple plaintiffs'],
    ['Net-worth', `${isMobile ? 'Net-worth' : 'Adverse party net-worth'}: ${d.adversePartyNetWorth ? getNetWorth(d.adversePartyNetWorth) || 'Not available' : 'Not available'}`, isMobile ? 'Net-worth' : 'Adverse party net-worth'],
    ['Entity', d.isEntity === 'yes' ? 'Entity' : 'Individual', 'Entity/Individual'],
    ...(d.adversePartyPublicCompany || !premium ? [['Public', `${isMobile ? 'Public company' : 'Against public company'}: ${publicCo(d.adversePartyPublicCompany)}`, isMobile ? 'Public company' : 'Against public company']] : []),
  ];
  const tagCls = `font-plexsans font-medium ${isMobile ? 'text-[12px] py-[7px] px-[9px]' : 'text-[13px] py-2 px-3'} leading-none text-navy bg-navy/[.05] border border-navy/[.12] rounded-[5px] whitespace-nowrap`;
  // mobile: the same facts as an even 2-column grid (label over value) so they line up
  const factList = [
    ['Class action', yesNo(d.isClassAction)],
    ['Multiple plaintiffs', yesNo(d.defendant)],
    ['Adverse party', d.isEntity === 'yes' ? 'Entity' : 'Individual'],
    ['Net-worth', d.adversePartyNetWorth ? getNetWorth(d.adversePartyNetWorth) || 'Not available' : 'Not available'],
    ...(d.adversePartyPublicCompany || !premium ? [['Public company', publicCo(d.adversePartyPublicCompany)]] : []),
  ];
  const facts = (
    <div className="grid grid-cols-2 gap-1.5">
      {factList.map(([label, value]) => (
        <div key={label}
          onClick={premium ? undefined : goSubscribe} title={premium ? undefined : upsell}
          className={`flex flex-col gap-[5px] py-2 px-2.5 rounded-[5px] bg-navy/[.04] border border-navy/[.1] min-w-0 [&:last-child:nth-child(odd)]:col-span-2 ${premium ? '' : 'cursor-pointer'}`}>
          <span className="font-plexsans font-medium text-[11px] leading-none text-navy/[.55] truncate">{label}</span>
          <span className="flex items-center gap-[5px] font-plexsans font-semibold text-[13px] leading-none text-navy truncate">
            {premium ? value : <><Blur>test</Blur><Svg d={ICON.lock} size={11} width={2.2} stroke="rgba(20,33,61,.45)" /></>}
          </span>
        </div>
      ))}
    </div>
  );
  const tags = (
    <div className={`flex flex-wrap ${isMobile ? 'gap-1.5 mb-0' : 'gap-2 mb-[18px]'}`}>
      {tagList.map(([k, text, label]) => premium
        ? <span key={k} className={tagCls}>{text}</span>
        : <span key={k} onClick={goSubscribe} title={upsell} className={`${tagCls} cursor-pointer inline-flex items-center gap-[5px]`}>{label}: <Blur>test</Blur><Svg d={ICON.lock} size={11} width={2.2} stroke="rgba(20,33,61,.45)" /></span>)}
    </div>
  );

  const namesRow = showNames ? (
    <div className={`flex flex-wrap ${isMobile ? 'gap-y-1.5 gap-x-3.5 text-[13px]' : 'gap-y-2 gap-x-7 text-[14px]'} font-plexsans font-normal leading-[1.4] text-navy/[.6] mb-3.5`}>
      <span>Potential client - <b className="font-semibold text-navy">{names?.client ?? (d.isOwnerAnonymous ? 'Anonymous' : (clientNameOf(d) || 'Not provided'))}</b></span>
      <span>Adverse party - <b className="font-semibold text-navy">{names?.adverse ?? (d.adversePartyName || '—')}</b></span>
    </div>
  ) : null;

  const stat = (icon, label, value, tip) => (
    <Tip text={tip}>
      <span className="inline-flex items-center gap-1.5 py-1.5 px-2.5 rounded-md bg-navy/[.05] border border-navy/[.14] whitespace-nowrap">
        <Svg d={icon} size={12} width={2} stroke={C.navy} />
        <span className="font-plexsans font-medium text-[11px] leading-none text-navy/[.65]">{label}</span>
        <span className="font-plexsans font-bold text-[11.5px] leading-none text-navy">{value ? value : 'None'}</span>
      </span>
    </Tip>
  );

  // ================= MOBILE (22e) =================
  if (isMobile) {
    const box = (v, word, label, tone) => (
      <div className={`flex-1 flex flex-col items-center gap-[5px] py-2.5 px-1.5 rounded-md border ${tone ? `${tone.bg} ${tone.border}` : 'bg-navy/[.04] border-navy/[.14]'}`}>
        <span className={`font-plexsans font-bold text-[18px] leading-none ${tone ? tone.text : 'text-navy'}`}>{v}<span className="text-[12px] font-medium text-navy/[.4]">/10</span></span>
        <span className={`font-plexsans font-semibold text-[11px] leading-none ${tone ? tone.text : 'text-navy'}`}>{label} · {word}</span>
      </div>
    );
    return (
      <div className={`bg-white border border-navy/[.12] rounded-lg overflow-hidden relative ${dimmed ? 'opacity-[.55]' : 'opacity-100'}`}>
        {overlay}
        <div className="bg-brass/[.08] border-b border-brass/[.18] pt-3.5 pb-4 px-[18px]">
          <div className="flex justify-between items-center gap-2 min-h-[24px] mb-3">
            <span onClick={copyId} className="font-plexmono font-medium text-[11.5px] leading-none tracking-[.06em] uppercase text-brass">{d.caseNumber}</span>
            <span className="flex items-center gap-2 flex-none">
              {headerPill || (posted && <span className="font-plexsans font-medium text-[11.5px] leading-none text-navy/[.55]">Posted {moment(posted).format('MM/DD/YY')}</span>)}
              {headerExtra}
            </span>
          </div>
          {savedTag && <div className="mb-2.5">{savedTag}</div>}
          <div className="font-plexsans font-semibold text-[17px] leading-[1.3] text-navy">{getCaseType(d.caseType)}</div>
          {lockedFirm
            ? <div className="flex items-center gap-2 mt-2">
                <span className="font-plexsans font-medium text-[11px] leading-none uppercase tracking-[.06em] text-navy/[.5]">Est. value</span>
                {estimateLocked}
              </div>
            : <div className="flex items-baseline gap-2 mt-1.5">
                <span className="font-plexsans font-medium text-[11px] leading-none uppercase tracking-[.06em] text-navy/[.5]">Est. value</span>
                <span className="font-plexsans font-bold text-[17px] leading-[1.3] text-navy tabular-nums">{estimateNode}</span>
              </div>}
        </div>
        <div className="py-4 px-[18px] flex flex-col gap-3.5">
          {notices && <div className="flex flex-col gap-2 items-start">{notices}</div>}
          {namesRow}
          <div className="flex flex-col gap-[11px] p-3.5 border border-navy/[.14] rounded-md">
            {rows.map((r) => (
              <div key={r.label} className="flex justify-between items-center gap-2.5">
                <span className="flex items-center gap-2 font-plexsans font-normal text-[13px] leading-[1.3] text-navy/[.6] whitespace-nowrap">
                  <Svg d={r.icon} size={15} stroke={r.urgent ? C.red : 'rgba(20,33,61,.45)'} />{r.short}
                </span>
                <span className={`font-plexsans font-semibold text-[13px] leading-[1.3] text-right ${r.urgent ? 'text-danger' : 'text-navy'} ${r.cap ? 'capitalize' : ''}`}>{r.value || '—'}</span>
              </div>
            ))}
          </div>
          {band}
          {(d.aiDifficulty || showSuit) &&
            <div className="flex gap-2.5">
              {d.aiDifficulty ? box(d.aiDifficulty, difficultyWord(d.aiDifficulty), 'Difficulty') : null}
              {showSuit ? (premium
                ? box(d.aiSuitability, suitabilityWord(d.aiSuitability), 'Funder fit', d.aiSuitability > 7 || d.aiSuitability <= 4 ? suitTone(d.aiSuitability) : null)
                : <div onClick={goSubscribe} className="flex-1 flex flex-col items-center gap-[5px] py-2.5 px-1.5 rounded-md bg-brass/[.06] border border-brass/[.3]">
                    <span className="font-plexsans font-bold text-[18px] leading-none text-brass"><Blur>00</Blur><span className="text-[12px] font-medium text-navy/[.4]">/10</span></span>
                    <span className="font-plexsans font-semibold text-[11px] leading-none text-brass">Funder fit · Premium</span>
                  </div>) : null}
            </div>}
          {!lockedFirm && facts}
          {!hideStats &&
            <div className="flex gap-3.5 font-plexsans font-medium text-[11.5px] leading-none text-navy/[.6]">
              <span>Requests <span className="font-bold text-navy">{requests || 'None'}</span></span>
              <span>In review <span className="font-bold text-navy">{review || 'None'}</span></span>
            </div>}
          {footerNote && <div className="font-plexsans font-normal text-[12px] leading-[1.4] text-navy/[.55]">{footerNote}</div>}
          {actions && <div className="flex flex-col gap-2">{actions}</div>}
        </div>
        {expanded && <div onClick={(e) => e.stopPropagation()}>{expanded}</div>}
      </div>
    );
  }

  // ================= DESKTOP (23c) =================
  const divider = <span className="w-px h-[22px] bg-brass/[.35] self-center" />;
  return (
    <div
      onClick={onOpen}
      className={`border-b border-navy/[.12] bg-white relative ${onOpen ? 'group/card cursor-pointer' : 'cursor-default'} ${dimmed ? 'opacity-[.55]' : 'opacity-100'}`}
    >
      {overlay}
      <div className="bg-brass/[.08] py-[18px] px-[30px] flex justify-between items-center gap-6 border-b border-brass/[.18]">
        <div className="flex items-center gap-3.5 flex-wrap min-w-0">
          <span onClick={copyId} title="Copy case number" className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy whitespace-nowrap cursor-copy">{d.caseNumber}</span>
          {divider}
          <span className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy whitespace-nowrap">{getCaseType(d.caseType)}</span>
          {divider}
          {lockedFirm
            ? estimateLocked
            : <span className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy whitespace-nowrap">{estimateNode}</span>}
        </div>
        <div className="flex-none flex items-center gap-2.5">
          {savedTag}
          {headerExtra}
          {headerPill || (posted &&
            <div className="flex items-center gap-[7px] py-[7px] px-3 rounded-[20px] bg-white border border-brass/[.25]">
              <Svg d={ICON.person} size={14} stroke={C.brass} />
              <span className="font-plexsans font-medium text-[12px] leading-none text-navy/[.7] whitespace-nowrap">{postedBy} · {moment(posted).format('MM/DD/YY')}</span>
            </div>)}
        </div>
      </div>
      <div className="pt-5 px-[30px] pb-[22px]">
        {notices && <div className="flex flex-wrap gap-2 mb-3.5">{notices}</div>}
        {(difficulty || suitability) && <div className="flex gap-2 mb-[18px] flex-wrap">{difficulty}{suitability}</div>}
        {namesRow}
        <div className="grid grid-cols-2 gap-y-3.5 gap-x-7 py-[18px] px-[22px] mb-5 border border-navy/[.14] rounded-md">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-2.5 font-plexsans font-normal text-[15.5px] leading-[1.4] text-navy/[.6] min-w-0">
              <Svg d={r.icon} size={17} stroke={r.urgent ? C.red : 'rgba(20,33,61,.45)'} />
              <span className="min-w-0">
                {r.label} - <span className={`font-semibold ${r.urgent ? 'text-danger' : 'text-navy'} ${r.cap ? 'capitalize' : ''}`}>{r.value || '—'}</span>
              </span>
            </div>
          ))}
        </div>
        {lockedFirm ? (band && <div className="mb-5">{band}</div>) : tags}
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div className="flex flex-col gap-2">
            {!hideStats &&
              <div className="flex gap-2.5">
                {stat(ICON.people, 'Requests', requests, 'Legal professionals who showed interest in this case')}
                {stat(ICON.eye, 'In review', review, 'Legal professionals who progressed to viewing this case')}
              </div>}
            {footerNote && <div className="font-plexsans font-normal text-[12.5px] leading-[1.4] text-navy/[.55]">{footerNote}</div>}
          </div>
          {actions && <div className="flex items-center gap-2.5 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>{actions}</div>}
        </div>
      </div>
      {expanded && <div onClick={(e) => e.stopPropagation()} className="cursor-default">{expanded}</div>}
    </div>
  );
}

export function pill(color, bg, border) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: '6px', font: `600 11.5px/1 ${C.font}`, color, background: bg,
    border: `1px solid ${border}`, borderRadius: '20px', padding: '6px 12px', whiteSpace: 'nowrap',
  };
}

// Header pill for a stage ("Approved by client · 09/24/26"). tone: green | brass | navy
export function StatusPill({ tone = 'navy', children }) {
  const isMobile = useIsMobile();
  const toneCls = tone === 'green' ? 'text-success border-success/[.35]' : tone === 'brass' ? 'text-brass border-brass/[.3]' : 'text-navy border-navy/[.2]';
  return (
    <span className={`inline-flex items-center font-plexsans font-semibold ${isMobile ? 'text-[11px] py-[5px] px-[9px]' : 'text-[12px] py-[7px] px-3'} leading-none bg-white border rounded-[20px] whitespace-nowrap ${toneCls}`}>
      {children}
    </span>
  );
}

export default CaseCard;
